import { useState, useEffect, useCallback } from "react";
import OrganisationLayout from "../layout/OrganisationLayout";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import { useSelector, useDispatch } from "react-redux";
import { schoolDetailAction } from "../../Redux/Action/AuthAction";
import { getOrganisationRoleAccess } from "../utils/roleAccess";
import InviteMemberOrganisationModal from "../../modals/InviteMemberOrganisationModal";
import {
  organisationGroupsListAction,
  organisationRemoveOrganisationAction,
} from "../../Redux/Action/OrganisationAction";

const formatDisplayDate = (value) => {
  if (!value) return "-";

  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDisplayDateTime = (value) => {
  if (!value) return "Never";

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getOrganisationId = (organisation) =>
  organisation?.organisationId ||
  organisation?.schoolId?._id ||
  organisation?.schoolId ||
  organisation?._id ||
  "";

const getOrganisationName = (organisation) =>
  organisation?.schoolName ||
  organisation?.organisationName ||
  organisation?.name ||
  "-";

function OrganisationDetailsModal({
  show,
  organisation,
  organisationDetail,
  isLoading,
  onHide,
}) {
  const d = organisationDetail || {};

  const ownerName = [d?.firstName, d?.surName].filter(Boolean).join(" ") || "-";
  const email = d?.email || d?.schoolEmail || "-";
  const address =
    [d?.address, d?.postCode || d?.postcode].filter(Boolean).join(", ") || "-";
  const status = d?.isBlocked ? "Blocked" : d?._id ? "Active" : "-";

  return (
    <Modal
      size="lg"
      show={show}
      onHide={onHide}
      className="comn-modal-set create"
      centered
    >
      <Modal.Header className="d-block" style={{ border: 0 }}>
        <Row>
          <Col lg={12} className="d-flex justify-content-center mb-4">
            <div>
              <Modal.Title className="p-0">Organisation Details</Modal.Title>
              <p className="text-center mb-0">
                Review the selected member organisation.
              </p>
            </div>
          </Col>
        </Row>
        <div className="modal-right">
          <button className="menu-dots" onClick={onHide}>
            <img
              src={require("../../Assets/Images/dotsweb.svg").default}
              alt="close"
            />
          </button>
        </div>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">
            Loading organisation details...
          </div>
        ) : (
          <>
            {/* ── Profile header ── */}
            <div className="organisation-member-summary">
              <img
                className="organisation-member-avatar"
                src={
                  d?.image ||
                  d?.schoolLogo ||
                  require("../../Assets/Images/girl.png")
                }
                alt={d?.schoolName || "-"}
              />
              <div>
                <h3>
                  {d?.schoolName || getOrganisationName(organisation) || "-"}
                </h3>
                <p>{d?.schoolType || "-"}</p>
              </div>
            </div>

            {/* ── Detail grid ── */}
            <div className="organisation-member-detail-grid">
              <div className="organisation-member-detail-item">
                <span>Email</span>
                <strong>{email}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Phone Number</span>
                <strong>{d?.phoneNumber || "-"}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Address</span>
                <strong>{address}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Postcode</span>
                <strong>{d?.postCode || d?.postcode || "-"}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Account Owner</span>
                <strong>{ownerName}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Account Owner Email</span>
                <strong>
                  {d?.accountOwnerEmail || d?.accountEmail || "-"}
                </strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Account Owner Phone</span>
                <strong>{d?.accountOwnerPhoneNumber || "-"}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Max Tutors</span>
                <strong>{d?.maxTutors ?? "-"}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Max Students</span>
                <strong>{d?.maxStudents ?? "-"}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Platform Visits</span>
                <strong>{d?.platformVisits ?? "-"}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Status</span>
                <strong>{status}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Last Logged In</span>
                <strong>{formatDisplayDateTime(d?.lastLoggedIn)}</strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Created At</span>
                <strong>
                  {formatDisplayDate(
                    d?.createdAt || organisation?.joinedPeerscroller,
                  )}
                </strong>
              </div>
              <div className="organisation-member-detail-item">
                <span>Joined Organisation Group</span>
                <strong>
                  {formatDisplayDate(organisation?.joinedOrganisationGroup)}
                </strong>
              </div>
            </div>

            {/* ── QR codes (optional) ── */}
            {(d?.qr?.student || d?.qr?.parent) && (
              <div className="double-qr-set mt-3">
                {d?.qr?.student && (
                  <div className="qr-code">
                    <img src={d.qr.student} alt="Student QR" />
                    <p>Student QR</p>
                  </div>
                )}
                {d?.qr?.parent && (
                  <div className="qr-code">
                    <img src={d.qr.parent} alt="Parent QR" />
                    <p>Parent QR</p>
                  </div>
                )}
              </div>
            )}
            {(d?.studentCode || d?.parentCode) && (
              <div className="organisation-member-detail-grid mt-2">
                {d?.studentCode && (
                  <div className="organisation-member-detail-item">
                    <span>Student Code</span>
                    <strong>{d.studentCode}</strong>
                  </div>
                )}
                {d?.parentCode && (
                  <div className="organisation-member-detail-item">
                    <span>Parent Code</span>
                    <strong>{d.parentCode}</strong>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default function OrganisationGroupsMemberOrganisations() {
  const userData = useSelector((state) => state?.authSlice.userData?.payload);
  const access = getOrganisationRoleAccess(userData);
  const dispatch = useDispatch();
  const organisationGroupId = userData?.organisationGroupId?._id;
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeRemoveId, setActiveRemoveId] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const [organisationDetail, setOrganisationDetail] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableMeta, setTableMeta] = useState({
    total: 0,
    page: 0,
    limit: 10,
    totalPages: 0,
  });
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === "asc";
    setSortBy(field);
    setSortOrder(isAsc ? "desc" : "asc");
    setPage(0);
  };

  const fetchOrganisations = useCallback(
    async (search = searchValue) => {
      if (!organisationGroupId) {
        setTableData([]);
        return;
      }

      setIsLoading(true);

      const response = await dispatch(
        organisationGroupsListAction({
          groupId: organisationGroupId,
          page,
          limit: tableMeta.limit || 10,
          search: search.trim(),
          sortBy,
          sortOrder,
        }),
      );

      const payload = response?.payload?.data;
      const organisations =
        payload?.organisations ||
        payload?.organisationsList ||
        payload?.rows ||
        payload?.data ||
        [];

      if (payload) {
        setTableData(Array.isArray(organisations) ? organisations : []);
        setTableMeta({
          total: Number(payload?.total || organisations.length || 0),
          page: Number(payload?.page || page),
          limit: Number(payload?.limit || 10),
          totalPages: Number(payload?.totalPages || 1),
        });
      } else {
        setTableData([]);
      }

      setIsLoading(false);
    },
    [dispatch, organisationGroupId, sortBy, sortOrder, page, tableMeta.limit, searchValue],
  );

  useEffect(() => {
    fetchOrganisations(searchValue);
  }, [fetchOrganisations, searchValue]);

  const handleModalClose = () => {
    setShowInviteModal(false);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedOrganisation(null);
    setOrganisationDetail(null);
    setIsDetailLoading(false);
  };

  const handleViewDetails = async (organisation) => {
    const organisationId = getOrganisationId(organisation);

    setShowDetailsModal(true);
    setSelectedOrganisation(organisation);
    setOrganisationDetail(null);

    if (!organisationId) {
      return;
    }

    setIsDetailLoading(true);

    const response = await dispatch(
      schoolDetailAction({
        queryParams: {
          id: organisationId,
        },
      }),
    );

    console.log("schoolDetailAction organisation response", response?.payload);

    if (!response?.error) {
      setOrganisationDetail(response?.payload?.data || {});
    }

    setIsDetailLoading(false);
  };

  const handleRemoveOrganisation = async (row) => {
    const schoolId = getOrganisationId(row);

    if (!organisationGroupId || !schoolId) {
      console.log("remove organisation skipped", {
        organisationGroupId,
        schoolId,
        row,
      });
      return;
    }

    setActiveRemoveId(schoolId);

    const response = await dispatch(
      organisationRemoveOrganisationAction({
        groupId: organisationGroupId,
        schoolId,
      }),
    );

    console.log(
      "organisationRemoveOrganisationAction response",
      response?.payload,
    );

    setActiveRemoveId("");

    if (!response?.error) {
      fetchOrganisations(searchValue);
    }
  };

  return (
    <OrganisationLayout>
      <Container fluid>
        <div className="heading-top mt-5 mb-2">
          <h2 style={{ fontFamily: "Raleway, sans-serif" }}>Member Organisations</h2>
        </div>

        {/* <div className="organisation-page-intro">
          <p>
            Manage the organisations inside the group with the same card depth,
            button styles, and spacing already used across the portal.
          </p>
        </div> */}

        {/* <Row className="g-4">
          <Col md={3}>
            <div className="organisation-groups-card organisation-stat-card organisation-stat-card-compact">
              <span className="organisation-card-kicker">
                Active Organisations
              </span>
              <strong className="organisation-groups-stat">
                {tableMeta.total}
              </strong>
              <p>Member organisations currently returned for this group.</p>
            </div>
          </Col>
          <Col md={3}>
            <div className="organisation-groups-card organisation-stat-card organisation-stat-card-compact">
              <span className="organisation-card-kicker">Teacher Accounts</span>
              <strong className="organisation-groups-stat">
                {totalTeacherCount}
              </strong>
              <p>Total teacher and admin accounts across members.</p>
            </div>
          </Col>
          <Col md={3}>
            <div className="organisation-groups-card organisation-stat-card organisation-stat-card-compact">
              <span className="organisation-card-kicker">Learner Accounts</span>
              <strong className="organisation-groups-stat">
                {totalLearnerCount}
              </strong>
              <p>Combined learner footprint across member organisations.</p>
            </div>
          </Col>
          <Col md={3}>
            <div className="organisation-groups-card organisation-stat-card organisation-stat-card-compact">
              <span className="organisation-card-kicker">Pending Invites</span>
              <strong className="organisation-groups-stat">
                {pendingInvitesCount}
              </strong>
              <p>Outstanding organisation invitations awaiting action.</p>
            </div>
          </Col>
        </Row> */}

        <Row className="g-3 align-items-center mt-1">
          <Col xl={4} lg={5}>
            <div className="organisation-search-shell">
              <input
                type="text"
                placeholder="Search Member Organisations"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
          </Col>
          {access.canInviteMemberOrganisation && (
            <Col
              xl={8}
              lg={7}
              className="create-new-btn d-flex justify-content-end"
            >
              <button
                className="cmn-pink"
                onClick={() => setShowInviteModal(true)}
              >
                Invite Member Organisation
              </button>
            </Col>
          )}
        </Row>

        {!access.canInviteMemberOrganisation && (
          <div className="organisation-note-banner">
            Your role can view member organisations but cannot invite new ones.
          </div>
        )}

        <section className="organisation-groups-table-shell organisation-groups-table-shell-space">
          <div className="organisation-panel-heading">
            <div>
              <h3 style={{ fontFamily: "Raleway, sans-serif" }}>Organisation list</h3>
            </div>
          </div>
          <div className="organisation-table-responsive">
            <table>
              <thead>
                <tr>
                  <th>
                    <span
                      className={`cursor-pointer ${
                        sortBy === "organisationName" ? sortOrder : ""
                      }`}
                      onClick={() => handleSort("organisationName")}
                    >
                      Member Organisation
                    </span>
                  </th>
                  <th>
                    <span
                      className={`cursor-pointer ${
                        sortBy === "joinedPeerscroller" ? sortOrder : ""
                      }`}
                      onClick={() => handleSort("joinedPeerscroller")}
                    >
                      Joined Peerscroller
                    </span>
                  </th>
                  <th>
                    <span
                      className={`cursor-pointer ${
                        sortBy === "joinedOrganisationGroup" ? sortOrder : ""
                      }`}
                      onClick={() => handleSort("joinedOrganisationGroup")}
                    >
                      Joined Organisation Group
                    </span>
                  </th>
                  <th>
                    <span
                      className={`cursor-pointer ${
                        sortBy === "learnerCount" ? sortOrder : ""
                      }`}
                      onClick={() => handleSort("learnerCount")}
                    >
                      Number Of Learners
                    </span>
                  </th>
                  <th>
                    <span
                      className={`cursor-pointer ${
                        sortBy === "parentCount" ? sortOrder : ""
                      }`}
                      onClick={() => handleSort("parentCount")}
                    >
                      Number Of Parents
                    </span>
                  </th>
                  <th>
                    <span
                      className={`cursor-pointer ${
                        sortBy === "teacherCount" ? sortOrder : ""
                      }`}
                      onClick={() => handleSort("teacherCount")}
                    >
                      Number Of Teachers
                    </span>
                  </th>
                  {access.canRemoveMemberOrganisation && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      Loading organisations...
                    </td>
                  </tr>
                )}
                {!isLoading && tableData.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      No member organisations found.
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  tableData.map((row) => (
                    <tr
                      key={`${row?.organisationName}-${row?.joinedOrganisationGroup}`}
                    >
                      <td>
                        <div
                          className="organisation-name-cell cursor-pointer"
                          onClick={() => handleViewDetails(row)}
                        >
                          <strong>{row?.organisationName || "-"}</strong>
                        </div>
                      </td>
                      <td>{formatDisplayDate(row?.joinedPeerscroller)}</td>
                      <td>{formatDisplayDate(row?.joinedOrganisationGroup)}</td>
                      <td>{row?.learnerCount ?? 0}</td>
                      <td>{row?.parentCount ?? 0}</td>
                      <td>{row?.teacherCount ?? 0}</td>
                      {access.canRemoveMemberOrganisation && (
                        <td>
                          <button
                            className="cmn-pink"
                            onClick={() => handleRemoveOrganisation(row)}
                            disabled={activeRemoveId === getOrganisationId(row)}
                          >
                            {activeRemoveId === getOrganisationId(row)
                              ? "Removing..."
                              : "Remove from Group"}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="organisation-pagination">
            <div className="organisation-pagination-meta">
              Showing {tableMeta.total === 0 ? 0 : page * tableMeta.limit + 1}-
              {Math.min((page + 1) * tableMeta.limit, tableMeta.total)} of{" "}
              {tableMeta.total || 0} organisations
            </div>
            <div className="organisation-pagination-actions">
              <button
                className="organisation-pagination-btn"
                onClick={() =>
                  setPage((prevState) => Math.max(prevState - 1, 0))
                }
                disabled={page === 0 || isLoading}
              >
                Previous
              </button>
              <span className="organisation-pagination-page">
                Page {page + 1}
                {tableMeta.totalPages > 0 ? ` of ${tableMeta.totalPages}` : ""}
              </span>
              <button
                className="organisation-pagination-btn"
                onClick={() => setPage((prevState) => prevState + 1)}
                disabled={
                  isLoading ||
                  tableData.length === 0 ||
                  (tableMeta.totalPages > 0 && page + 1 >= tableMeta.totalPages)
                }
              >
                Next
              </button>
            </div>
          </div>
        </section>
      </Container>
      <OrganisationDetailsModal
        show={showDetailsModal}
        organisation={selectedOrganisation}
        organisationDetail={organisationDetail}
        isLoading={isDetailLoading}
        onHide={handleCloseDetailsModal}
      />
      <InviteMemberOrganisationModal
        show={showInviteModal}
        handleClose={handleModalClose}
        onInviteSent={() => fetchOrganisations(searchValue)}
      />
    </OrganisationLayout>
  );
}
